import { Injectable, OnDestroy } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  GetTokenOptions,
  Messaging,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  DELETE_NOTIFICATION_DEVICE_ENDPOINT,
  POST_NOTIFICATION_DEVICE_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { TokenService } from './token.service';

export type BrowserPushState = 'unsupported' | 'not-configured' | 'prompt' | 'enabled' | 'blocked';

export interface FirebaseEvent {
  kind: 'notification' | 'chat' | string;
  event: string;
  title?: string;
  body?: string;
  notification_id?: string;
  resource_id?: string;
  account_id?: string;
  conversation_id?: string;
  route?: string;
  sender_name?: string;
  message_preview?: string;
}

const FIREBASE_MESSAGE_TYPE = 'firebase.message';
const FIREBASE_BROADCAST_CHANNEL = 'workwife-firebase';
export const EMAIL_CLIENT_MESSAGE_RECEIVED_NOTIFICATION =
  'user:email-client.message.received';

@Injectable({ providedIn: 'root' })
export class FirebaseService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<BrowserPushState>('unsupported');
  private readonly eventsSubject = new Subject<FirebaseEvent>();
  private activeAccessToken?: string;
  private currentDeviceToken?: string;
  private foregroundMessageUnsubscribe?: () => void;
  private serviceWorkerMessageListening = false;
  private broadcastChannel?: BroadcastChannel;

  private readonly handleServiceWorkerMessage = (event: MessageEvent): void => {
    if (event.data?.type !== FIREBASE_MESSAGE_TYPE) return;
    this.publish(event.data?.data);
  };

  readonly state$ = this.stateSubject.asObservable();
  readonly events$ = this.eventsSubject.asObservable();

  constructor(
    private readonly restApi: RestApiService,
    private readonly tokenService: TokenService,
  ) {}

  ngOnDestroy(): void {
    this.resetRuntimeListeners();
  }

  start(requestPermission = true): void {
    const accessToken = this.tokenService.getAccessToken();
    if (!accessToken || this.activeAccessToken === accessToken) return;
    this.resetRuntimeListeners();
    this.activeAccessToken = accessToken;
    void this.configure(requestPermission).catch(() => {
      this.setState(Notification.permission === 'denied' ? 'blocked' : 'prompt');
    });
  }

  async stop(deactivateDevice = false): Promise<void> {
    const token = this.currentDeviceToken;
    if (deactivateDevice && token && this.tokenService.getAccessToken()) {
      try {
        await firstValueFrom(
          this.restApi.deleteWithBody(DELETE_NOTIFICATION_DEVICE_ENDPOINT, { token }),
        );
      } catch {
        // Listener cleanup must still complete when device deactivation is unavailable.
      }
    }
    this.activeAccessToken = undefined;
    this.currentDeviceToken = undefined;
    this.resetRuntimeListeners();
  }

  enable(): Promise<BrowserPushState> {
    return this.configure(true);
  }

  private async configure(requestPermission: boolean): Promise<BrowserPushState> {
    if (!(await isSupported()) || !('serviceWorker' in navigator)) {
      return this.setState('unsupported');
    }
    const config = environment.firebase;
    if (!config.apiKey || !config.projectId || !config.messagingSenderId || !config.appId) {
      return this.setState('not-configured');
    }
    let permission = Notification.permission;
    if (permission === 'default' && requestPermission) {
      permission = await Notification.requestPermission();
    }
    if (permission === 'denied') return this.setState('blocked');
    if (permission !== 'granted') return this.setState('prompt');

    const app: FirebaseApp = getApps().length
      ? getApp()
      : initializeApp({
          apiKey: config.apiKey,
          authDomain: config.authDomain,
          projectId: config.projectId,
          storageBucket: config.storageBucket,
          messagingSenderId: config.messagingSenderId,
          appId: config.appId,
        });
    const messaging: Messaging = getMessaging(app);
    const serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const tokenOptions: GetTokenOptions = { serviceWorkerRegistration };
    if (config.vapidKey) tokenOptions.vapidKey = config.vapidKey;
    const token = await getToken(messaging, tokenOptions);
    if (!token) return this.setState('prompt');

    await firstValueFrom(
      this.restApi.post(POST_NOTIFICATION_DEVICE_ENDPOINT, {
        token,
        platform: 'web',
        app: 'main',
      }),
    );
    this.currentDeviceToken = token;
    this.listenForMessages(messaging);
    return this.setState('enabled');
  }

  private listenForMessages(messaging: Messaging): void {
    if (!this.serviceWorkerMessageListening) {
      navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage);
      this.serviceWorkerMessageListening = true;
    }
    if ('BroadcastChannel' in globalThis && !this.broadcastChannel) {
      this.broadcastChannel = new BroadcastChannel(FIREBASE_BROADCAST_CHANNEL);
      this.broadcastChannel.addEventListener('message', this.handleServiceWorkerMessage);
    }
    this.foregroundMessageUnsubscribe?.();
    this.foregroundMessageUnsubscribe = onMessage(messaging, (payload) => {
      this.publish({
        ...payload.data,
        title: payload.notification?.title,
        body: payload.notification?.body,
      });
    });
  }

  private publish(data: unknown): void {
    if (!data || typeof data !== 'object') return;
    const event = data as FirebaseEvent;
    if (!event.event && !event.kind) return;
    this.eventsSubject.next(event);
  }

  private resetRuntimeListeners(): void {
    this.foregroundMessageUnsubscribe?.();
    this.foregroundMessageUnsubscribe = undefined;
    if (this.serviceWorkerMessageListening && 'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this.handleServiceWorkerMessage);
      this.serviceWorkerMessageListening = false;
    }
    this.broadcastChannel?.removeEventListener('message', this.handleServiceWorkerMessage);
    this.broadcastChannel?.close();
    this.broadcastChannel = undefined;
  }

  private setState(state: BrowserPushState): BrowserPushState {
    this.stateSubject.next(state);
    return state;
  }
}
